import { useState, useEffect, useCallback } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Globe, Upload, Image as ImageIcon, X, ExternalLink, Trophy, Medal, Phone } from "lucide-react";

const AUTH = () => ({
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const SECTIONS = [
  { key: "show_rankings", label: "Rankings", icon: Trophy, desc: "Mostrar los rankings activos del club" },
  { key: "show_tournaments", label: "Torneos", icon: Medal, desc: "Mostrar los torneos en la pagina" },
  { key: "show_contact", label: "Contacto", icon: Phone, desc: "Mostrar email y telefono del club" },
];

export default function ClubLanding() {
  const ctx = useClub();
  const isMaster = ctx?.clubUser?.club_role === "master";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ logo_url: null, landing: {} });
  const [uploading, setUploading] = useState({ banner: false, logo: false });

  const fetchLanding = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/landing`, { credentials: "include", headers: AUTH() });
      if (res.ok) setData(await res.json());
    } catch {
      toast.error("Error al cargar configuracion");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLanding(); }, [fetchLanding]);

  const updateLanding = async (partial) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/landing`, {
        method: "PUT", credentials: "include",
        headers: { ...AUTH(), "Content-Type": "application/json" },
        body: JSON.stringify(partial),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || "Error");
      setData(result);
      toast.success("Guardado");
    } catch (err) {
      toast.error(err.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e, assetType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((p) => ({ ...p, [assetType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", assetType);
      const res = await fetch(`${API}/club/landing/upload`, {
        method: "POST", credentials: "include", headers: AUTH(), body: fd,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || "Error");
      toast.success(`${assetType === "banner" ? "Banner" : "Logo"} actualizado`);
      fetchLanding();
    } catch (err) {
      toast.error(err.message || "Error subiendo imagen");
    } finally {
      setUploading((p) => ({ ...p, [assetType]: false }));
      e.target.value = "";
    }
  };

  const removeAsset = async (assetType) => {
    if (!window.confirm(`Eliminar ${assetType === "banner" ? "banner" : "logo"}?`)) return;
    if (assetType === "logo") {
      updateLanding({ logo_url: "" });
    } else {
      updateLanding({ banner_url: "" });
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const landing = data.landing || {};
  const previewUrl = `/${ctx?.club?.slug}`;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="landing-title">
            <Globe className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} /> Pagina Publica
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Configura como se ve tu micrositio en cadii.app/{ctx?.club?.slug}</p>
        </div>
        <a href={previewUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-[#1B4D3E] hover:underline" data-testid="preview-link">
          Ver pagina <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {!isMaster && (
        <Card className="card-default mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-sm text-amber-800">Solo el Master del club puede editar la pagina publica.</CardContent>
        </Card>
      )}

      {/* Banner */}
      <Card className="card-default mb-6">
        <CardContent className="p-5">
          <Label className="text-sm font-bold text-[#0F172A]">Banner</Label>
          <p className="text-xs text-[#94A3B8] mb-3">Imagen grande que aparece en el hero (1600x600 recomendado, max 8MB)</p>
          {landing.banner_url ? (
            <div className="relative rounded-xl overflow-hidden border border-gray-100" data-testid="banner-preview">
              <img src={landing.banner_url} alt="Banner" className="w-full h-40 object-cover" />
              {isMaster && (
                <button onClick={() => removeAsset("banner")}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center"
                  data-testid="remove-banner-btn"><X className="w-4 h-4" /></button>
              )}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center text-[#94A3B8]" data-testid="banner-empty">
              <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Sin banner</p>
            </div>
          )}
          {isMaster && (
            <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B4D3E] hover:bg-[#163F33] text-white text-sm font-medium cursor-pointer" data-testid="upload-banner-label">
              <Upload className="w-4 h-4" /> {uploading.banner ? "Subiendo..." : (landing.banner_url ? "Reemplazar banner" : "Subir banner")}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onUpload(e, "banner")} disabled={uploading.banner} data-testid="upload-banner-input" />
            </label>
          )}
        </CardContent>
      </Card>

      {/* Logo */}
      <Card className="card-default mb-6">
        <CardContent className="p-5">
          <Label className="text-sm font-bold text-[#0F172A]">Logotipo del Club</Label>
          <p className="text-xs text-[#94A3B8] mb-3">Cuadrado preferible (400x400, max 8MB) — aparece como avatar en el hero y en compartidos</p>
          <div className="flex items-center gap-4">
            {data.logo_url ? (
              <div className="relative" data-testid="logo-preview">
                <img src={data.logo_url} alt="Logo" className="w-24 h-24 rounded-2xl object-cover border border-gray-100 shadow-sm" />
                {isMaster && (
                  <button onClick={() => removeAsset("logo")}
                    className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow"
                    data-testid="remove-logo-btn"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gray-100 flex items-center justify-center" data-testid="logo-empty">
                <ImageIcon className="w-8 h-8 text-[#94A3B8] opacity-50" />
              </div>
            )}
            {isMaster && (
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-[#0F172A] text-sm font-medium cursor-pointer" data-testid="upload-logo-label">
                <Upload className="w-4 h-4" /> {uploading.logo ? "Subiendo..." : (data.logo_url ? "Reemplazar logo" : "Subir logo")}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onUpload(e, "logo")} disabled={uploading.logo} data-testid="upload-logo-input" />
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sections visibility */}
      <Card className="card-default">
        <CardContent className="p-5">
          <Label className="text-sm font-bold text-[#0F172A]">Secciones visibles</Label>
          <p className="text-xs text-[#94A3B8] mb-4">Desactiva las secciones que no quieres mostrar (ej. si no tienes rankings activos)</p>
          <div className="space-y-3" data-testid="sections-toggles">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const enabled = landing[s.key] !== false;
              return (
                <div key={s.key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100" data-testid={`section-${s.key}`}>
                  <div className="w-9 h-9 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#1B4D3E]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#0F172A]">{s.label}</p>
                    <p className="text-xs text-[#94A3B8]">{s.desc}</p>
                  </div>
                  <Switch
                    checked={enabled}
                    disabled={!isMaster || saving}
                    onCheckedChange={(v) => updateLanding({ [s.key]: v })}
                    data-testid={`toggle-${s.key}`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
